import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getTypographyFromTheme } from '../../../../utils/theme/getTypographyFromTheme';

export const FAKE_COLUMN_SIZE = 44;
export const CHECKBOX_COLUMN_WIDTH = 28;
export const FIRST_COLUMN_PADDING_WITH_CHECKBOX = 0.5;
export const FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX = 1;
export const CHECKBOX_COLUMN_PADDING = 0.5;
export const COLUMN_GAP = 1.5;
export const STICKY_GROUP_ROW_HEIGHT = 38;
export const BODY_ROW_MIN_HEIGHT = 44;

export const useStyles = makeStyles<
  void,
  | 'actionsIconButton'
  | 'actionsColumn'
  | 'stickyGroupCell'
  | 'fixedCell'
  | 'fixedCellSelected'
  | 'fixedRightCell'
  | 'fixedRightCellSelected'
  | 'cell'
  | 'disabledActionsIconButton'
  | 'emptyFixedColumn'
  | 'stickyCheckbox'
  | 'checkbox'
  | 'baseTableCellRow'
  | 'selectedRow'
  | 'parentRow'
  | 'rootParentRow'
  | 'horizontalScrollbar'
>()((theme, _, classes) => {
  const typographyBody1 = getTypographyFromTheme(theme.typography.body1);
  const selectedRowBackground = alpha(theme.palette.primary.main, 0.05);
  const selectedRowBackgroundHover = alpha(theme.palette.primary.main, 0.1);
  const selectedSurface = {
    backgroundColor: theme.palette.custom.layout.workspace,
    backgroundImage: `linear-gradient(${selectedRowBackground}, ${selectedRowBackground})`,
  };
  const selectedSurfaceHover = {
    backgroundColor: theme.palette.custom.layout.workspace,
    backgroundImage: `linear-gradient(${selectedRowBackgroundHover}, ${selectedRowBackgroundHover})`,
  };
  const typographyBody2 = getTypographyFromTheme(theme.typography.body2);

  return {
    fixedHeaderContainer: {
      width: '100%',
      position: 'sticky',
      zIndex: 2,
      backgroundColor: 'white',
      borderRadius: `${theme.shape.borderRadius2xl} ${theme.shape.borderRadius2xl} 0px 0px`,
      overflowY: 'hidden',
    },
    fixedHeader: {
      overflowX: 'hidden',
      display: 'flex',
    },
    tableRowHover: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
      },
      [`&:hover .${classes.actionsColumn}`]: {
        backgroundColor: theme.palette.action.hover,
        boxShadow: `none`,
        cursor: 'pointer',
      },
      [`&:hover .${classes.emptyFixedColumn}`]: {
        cursor: 'pointer',
        boxShadow: 'none !important',
      },
      [`&:hover .${classes.fixedCell}`]: {
        backgroundColor: theme.palette.action.hover,
        boxShadow: 'none',
        cursor: 'pointer',
      },
      [`&:hover .${classes.stickyCheckbox}`]: {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
      },
      [`&:hover .${classes.fixedCellSelected}`]: {
        ...selectedSurfaceHover,
        cursor: 'pointer',
      },
      [`&:hover .${classes.fixedRightCell}`]: {
        backgroundColor: theme.palette.action.hover,
        boxShadow: 'none',
        cursor: 'pointer',
      },
      [`&:hover .${classes.fixedRightCellSelected}`]: {
        ...selectedSurfaceHover,
        cursor: 'pointer',
      },
      [`&:hover .${classes.cell}`]: {
        backgroundColor: theme.palette.action.hover,
      },
      [`.${classes.actionsIconButton}:hover svg`]: {
        color: theme.palette.accent.dark,
        transition: theme.transitions.create('color', {
          duration: theme.transitions.duration.short,
          easing: theme.transitions.easing.easeInOut,
        }),
      },
      [`&:hover .${classes.disabledActionsIconButton}`]: {
        '&.MuiIconButton-root.Mui-disabled': {
          color: 'white',
          backgroundColor: theme.palette.grey[400],
        },
      },
    },
    cell: {
      cursor: 'default',
    },
    disablePointerCursor: {
      '&:hover': {
        cursor: 'default',
      },
    },
    parentRow: {
      '&:hover': {
        cursor: 'auto',
      },
    },
    rootParentRow: {
      position: 'relative',
      zIndex: 1,
      backgroundColor: theme.palette.background.paper,
    },
    stickyGroupHeaderContainer: {
      overflow: 'visible',
    },
    stickyGroupOverlay: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      overflow: 'hidden',
    },
    stickyGroupSlot: {
      position: 'absolute',
      left: 0,
      right: 0,
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
    },
    stickyGroupSlotContent: {
      height: '100%',
    },
    stickyGroupRow: {
      height: STICKY_GROUP_ROW_HEIGHT,
      backgroundColor: theme.palette.background.paper,
    },
    stickyGroupDeepestSlot: {
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 1,
        zIndex: 2,
        pointerEvents: 'none',
        backgroundColor: theme.palette.custom.border.primary,
      },
    },
    stickyGroupCell: {
      ...typographyBody1,
      height: STICKY_GROUP_ROW_HEIGHT,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      borderBottom: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: theme.palette.background.paper,
    },
    mobileSelectedRow: {
      backgroundColor: selectedRowBackground,
      [`& .${classes.actionsIconButton}`]: {
        color: '#4A4A4A',
      },
    },
    selectedRow: {
      backgroundColor: selectedRowBackground,
      [`&.${classes.rootParentRow}`]: selectedSurface,
      [`& .${classes.actionsIconButton}`]: {
        color: '#4A4A4A',
      },
      [`& .${classes.actionsColumn}`]: {
        ...selectedSurface,
        boxShadow: 'none',
      },
      [`& .${classes.fixedRightCell}`]: {
        ...selectedSurface,
        boxShadow: 'none',
      },
      [`& .${classes.stickyCheckbox}`]: selectedSurface,
      '&:hover': {
        backgroundColor: selectedRowBackgroundHover,
      },
      [`&:hover .${classes.actionsColumn},
        &:hover .${classes.stickyCheckbox}`]: selectedSurfaceHover,
    },
    actionsIconButton: {
      transition: 'none',
    },
    disabledActionsIconButton: {
      transition: 'none',
    },
    loadingRow: {
      height: '40px',
      padding: '0px 12px',
    },
    hideColumnsButton: {
      '&:hover': {
        backgroundColor: 'white',
      },
    },
    parentCell: {
      borderBottom: 'none',
    },
    checkbox: {
      width: CHECKBOX_COLUMN_WIDTH,
      minWidth: CHECKBOX_COLUMN_WIDTH,
      maxWidth: CHECKBOX_COLUMN_WIDTH,
      '&&': {
        paddingLeft: theme.spacing(CHECKBOX_COLUMN_PADDING),
      },
    },
    disabledCheckbox: {
      '&.Mui-disabled': {
        color: theme.palette.accent.light,
      },
    },
    baseTableCellRow: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(COLUMN_GAP),
      paddingRight: 0,
      borderBottom: 'none',
      ...typographyBody1,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      '& > *': {
        overflow: 'hidden',
      },
      [theme.breakpoints.only('xs')]: {
        ...typographyBody2,
      },
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
      },
    },
    sortLabel: {
      textTransform: 'none',
      overflow: 'hidden',
      userSelect: 'text',
      '& .MuiTableSortLabel-icon': {
        width: 14,
        height: 14,
      },
    },
    headerCell: {
      height: 40,
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: 0,
      paddingLeft: theme.spacing(FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX),
      '&:not(:first-child)': {
        paddingLeft: theme.spacing(COLUMN_GAP),
      },
      [`.${classes.checkbox} + &`]: {
        paddingLeft: theme.spacing(FIRST_COLUMN_PADDING_WITH_CHECKBOX),
      },
    },
    actionsColumn: {
      backgroundColor: 'white',
      boxShadow: '-6px 0 10px -2px white',
      position: 'sticky',
      right: 0,
      width: FAKE_COLUMN_SIZE,
      minWidth: FAKE_COLUMN_SIZE,
      maxWidth: FAKE_COLUMN_SIZE,
      '&.MuiTableCell-paddingCheckbox': {
        paddingInline: theme.spacing(1),
      },
    },
    emptyFixedColumn: {
      right: 0,
      width: 16,
      minWidth: 16,
      maxWidth: 16,
    },
    fixedCell: {
      position: 'sticky',
      left: 0,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `-6px 0 10px -2px ${theme.palette.background.paper}`,
      zIndex: 1,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
      },
    },
    fixedCellSelected: {
      ...selectedSurface,
      boxShadow: 'none',
    },
    fixedRightCell: {
      position: 'sticky',
      right: 0,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `-6px 0 10px -2px ${theme.palette.background.paper}`,
      zIndex: 1,
    },
    fixedRightCellSelected: {
      ...selectedSurface,
      boxShadow: 'none',
    },
    fixedRightScrolledCell: {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    fixedScrolledCell: {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    stickyCheckbox: {
      left: 0,
      zIndex: 1,
      position: 'sticky',
      backgroundColor: 'white',
    },
    tableCellClickable: {
      '&.MuiTableCell-body:hover a': {
        color: 'inherit',
        fontWeight: 'inherit',
        textDecoration: 'none',
      },
    },
    table: {
      borderCollapse: 'separate',
      '& *': {
        'box-sizing': 'border-box',
      },
      '& .MuiInputBase-input': {
        'box-sizing': 'content-box',
      },
      '& .MuiTypography-root': {
        lineHeight: 'normal',
      },
      minWidth: '100%',
      [`& tr.${classes.selectedRow} + tr.${classes.selectedRow} .${classes.baseTableCellRow}:first-child`]:
        {
          borderTopLeftRadius: 0,
        },
      [`& tr.${classes.selectedRow} + tr.${classes.selectedRow} .${classes.baseTableCellRow}:last-child`]:
        {
          borderTopRightRadius: 0,
        },
      [`& tr.${classes.selectedRow}:has(+ tr.${classes.selectedRow}) .${classes.baseTableCellRow}:first-child`]:
        {
          borderBottomLeftRadius: 0,
        },
      [`& tr.${classes.selectedRow}:has(+ tr.${classes.selectedRow}) .${classes.baseTableCellRow}:last-child`]:
        {
          borderBottomRightRadius: 0,
        },
    },
    scroller: {
      backgroundColor: theme.palette.custom.layout.workspace,
      overflowX: 'auto',
      overflowY: 'hidden',
      whiteSpace: 'nowrap',
      width: '100%',
      flexGrow: 1,
      flexShrink: 0,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    scrollerWrapper: {
      '@media (hover: hover)': {
        [`&:hover .${classes.horizontalScrollbar}`]: {
          '--scrollbar-thumb-color': theme.palette.action.active,
        },
      },
    },
    horizontalScrollbar: {},
    bodyCell: {
      '&&': {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
      },
    },
    bodyCellContent: {
      minHeight: BODY_ROW_MIN_HEIGHT,
    },
    cellContent: {
      display: 'block',
      alignContent: 'center',
      boxSizing: 'border-box',
      height: '100%',
      paddingBlock: theme.spacing(0.5),
      whiteSpace: 'inherit',
      overflow: 'inherit',
      textOverflow: 'inherit',
      userSelect: 'text',
      WebkitUserDrag: 'none',
    },
    withExtraHeader: {
      borderTop: 'none',
    },
    extraHeader: {
      paddingBlock: theme.spacing(1),
      borderBottom: 'none',
    },
    mobileGridItemLink: {
      textDecoration: 'none',
      ...typographyBody1,
      lineHeight: 'normal',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [theme.breakpoints.only('xs')]: {
        ...typographyBody2,
        lineHeight: 'normal',
      },
    },
    mobileLink: {
      textDecoration: 'inherit',
      color: 'inherit',
      whiteSpace: 'inherit',
      overflow: 'inherit',
      textOverflow: 'inherit',
    },
    mobileCard: {
      display: 'flex',
      flexDirection: 'column',
      border: `0.5px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
    },
    mobileCardFields: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      columnGap: theme.spacing(1.5),
      rowGap: theme.spacing(1),
      alignItems: 'center',
      paddingTop: theme.spacing(0.75),
      paddingInline: theme.spacing(1.5),
    },
    mobileCardActionArea: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      textDecoration: 'none',
      textTransform: 'none',
      paddingBottom: theme.spacing(0.5),
      '& .MuiCardActionArea-focusHighlight': {
        background: 'transparent',
      },
    },
  };
});
